// ── Messaging contract (REQ-21) ─────────────────────────────────────────────
// LIVE backend integration (swapped from the mock-first build). Two-way,
// property-scoped conversations between a citizen and company staff, with
// photo/PDF attachments. Backend models this as `PortalConversation` (a facade
// over `CompanyEmailAccountMessage`); schemas subclass `CamelModel`, so the
// wire shape is camelCase and matches the contract types below verbatim.
// An optional `ticketId` links a conversation to a ticket.
//
// Error contract: the backend returns a flat `{ code, message }` on domain
// errors; `raise()` maps that to `MessagesError('CODE')` so call sites keep
// their `isMockError(e) && e.code === …` branches. Anything without a string
// `code` (401 `{ detail }`, 422 `{ detail: [] }`) rethrows as the raw axios
// error, which `toErrorCode` in the composer maps by HTTP status.
import axios from 'axios';
import { apiClient } from 'src/api/client';
import { MockDomainError } from 'src/api/_mock/util';

export class MessagesError extends MockDomainError {}

// ── Contract types (= the real backend shapes) ──────────────────────────────
export type Author = 'customer' | 'company';

export type Attachment = {
  id: string;
  url: string;
  mimeType: string;
  // Backend serializes this as Optional[str] (can be null); render sites must guard.
  name: string | null;
  size: number;
};

export type Message = {
  id: string;
  author: Author;
  authorName: string;
  body: string;
  attachments: Attachment[];
  createdAt: string; // ISO
};

export type ConversationSummary = {
  id: string;
  subject: string;
  lastMessagePreview: string;
  lastMessageAt: string; // ISO
  unread: boolean;
  ticketId?: string;
};

export type ConversationDetail = {
  id: string;
  subject: string;
  ticketId?: string;
  messages: Message[];
};

// ── Attachment constraints (FE fail-fast validation; backend re-enforces) ────
export const MAX_ATTACHMENT_BYTES = 10 * 1024 * 1024; // 10 MB
export const MAX_ATTACHMENTS_PER_MESSAGE = 5;
export const isAllowedAttachment = (type: string) =>
  type.startsWith('image/') || type === 'application/pdf';

// ── Error adapter ────────────────────────────────────────────────────────────
// Map the backend's flat `{ code, message }` domain-error envelope to
// MessagesError so call sites keep working via `isMockError`. Anything without
// a string `code` rethrows as-is.
const raise = (err: unknown): never => {
  if (axios.isAxiosError(err)) {
    const data = err.response?.data as
      | { code?: unknown; message?: unknown }
      | undefined;
    if (data && typeof data.code === 'string') {
      throw new MessagesError(
        data.code,
        typeof data.message === 'string' ? data.message : undefined
      );
    }
  }
  throw err;
};

// ── Endpoints ───────────────────────────────────────────────────────────────

export async function getConversations(input: {
  propertyId: string;
}): Promise<ConversationSummary[]> {
  try {
    return (
      await apiClient.get<ConversationSummary[]>(
        `property/${input.propertyId}/conversations`
      )
    ).data;
  } catch (e) {
    return raise(e);
  }
}

export async function getConversation(input: {
  propertyId: string;
  id: string;
}): Promise<ConversationDetail> {
  try {
    return (
      await apiClient.get<ConversationDetail>(
        `property/${input.propertyId}/conversations/${input.id}`
      )
    ).data;
  } catch (e) {
    return raise(e);
  }
}

export async function startConversation(input: {
  propertyId: string;
  subject: string;
  body: string;
  attachmentIds: string[];
}): Promise<ConversationDetail> {
  try {
    return (
      await apiClient.post<ConversationDetail>(
        `property/${input.propertyId}/conversations`,
        {
          subject: input.subject,
          body: input.body,
          attachmentIds: input.attachmentIds,
        }
      )
    ).data;
  } catch (e) {
    return raise(e);
  }
}

export async function sendMessage(input: {
  propertyId: string;
  conversationId: string;
  body: string;
  attachmentIds: string[];
}): Promise<Message> {
  try {
    return (
      await apiClient.post<Message>(
        `property/${input.propertyId}/conversations/${input.conversationId}/messages`,
        { body: input.body, attachmentIds: input.attachmentIds }
      )
    ).data;
  } catch (e) {
    return raise(e);
  }
}

export async function markRead(input: {
  propertyId: string;
  conversationId: string;
}): Promise<void> {
  try {
    await apiClient.post(
      `property/${input.propertyId}/conversations/${input.conversationId}/read`
    );
  } catch (e) {
    raise(e);
  }
}

export async function uploadAttachment(input: {
  propertyId: string;
  file: File;
}): Promise<Attachment> {
  // Fail fast on invalid files so we don't push 10+ MB just to be rejected;
  // the backend re-checks and is the source of truth (same codes).
  if (!isAllowedAttachment(input.file.type)) {
    throw new MessagesError('UNSUPPORTED_TYPE');
  }
  if (input.file.size > MAX_ATTACHMENT_BYTES) {
    throw new MessagesError('FILE_TOO_LARGE');
  }
  try {
    const form = new FormData();
    form.append('file', input.file);
    return (
      await apiClient.post<Attachment>(
        `property/${input.propertyId}/attachments`,
        form,
        { headers: { 'Content-Type': 'multipart/form-data' } }
      )
    ).data;
  } catch (e) {
    return raise(e);
  }
}

export async function getUnreadCount(input: {
  propertyId: string;
}): Promise<{ count: number }> {
  try {
    return (
      await apiClient.get<{ count: number }>(
        `property/${input.propertyId}/conversations/unread-count`
      )
    ).data;
  } catch (e) {
    return raise(e);
  }
}
